import { ReactNode, useRef, useEffect } from 'react';
import StopOutlined from '@ant-design/icons/lib/icons/StopOutlined';
import EyeOutlined from '@ant-design/icons/lib/icons/EyeOutlined';
import { useTranslation } from 'react-i18next';

import { opTime } from 'utils/dates';
import { OpTypography } from 'new-components/DLS/OpTypography/OpTypography';
import { OpList } from 'new-components/DLS/OpList/OpList';
import { OpModal } from 'new-components/DLS/OpModal/OpModal';
import { useSelectorJs } from 'utils/customHooks';

import {
  DATE_FORMAT,
  DATE_TIME_AM_PM_FORMAT,
} from '../../../../constants/dates';
import { ImmutableRootState } from '../../../../store/store';
import { RedFlag } from '../../../../types/redFlagTypes';
import { RedFlagState } from '../../../../store/slices/redFlagSlice';
import { capitalizeName } from '../../../../utils/utils';

const { Text } = OpTypography;

interface RedFlagDetailsModalProps {
  open: boolean;
  onClose: () => void;
  redFlagData: RedFlag | null;
}

const RedFlagDetailsModal = ({
  open,
  onClose,
  redFlagData,
}: RedFlagDetailsModalProps) => {
  const { t } = useTranslation();

  const redFlagLogs =
    (useSelectorJs((state: ImmutableRootState) =>
      state.getIn(['redFlags', 'redFlagLogs']),
    ) as RedFlagState['redFlagLogs']) || [];
  const scrollableContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && scrollableContainerRef.current) {
      scrollableContainerRef.current.scrollTop = 0;
    }
  }, [open]);

  // Sort logs in descending order by timestamp
  const sortedLogs = redFlagLogs.slice().sort((a, b) => {
    if (a.timestamp && b.timestamp) {
      return opTime(b.timestamp).diff(opTime(a.timestamp));
    }
    return 0;
  });

  const renderListItem = (label: string, value: ReactNode) => {
    if (value !== null && value !== undefined) {
      return (
        <OpList.Item>
          <Text strong>{label}:</Text> {value}
        </OpList.Item>
      );
    }
    return null;
  };

  const getLevelNameDisplay = (levelName: string) => {
    if (levelName === 'Blocklist') {
      return (
        <span>
          <StopOutlined style={{ color: 'rgba(242,103,87,1.0)' }} /> {levelName}
        </span>
      );
    }
    if (levelName === 'Watchlist') {
      return (
        <span>
          <EyeOutlined style={{ color: '#FFD54F' }} /> {levelName}
        </span>
      );
    }
    return levelName;
  };

  const profileData = [
    {
      label: t('visitors.redFlag.modal.fields.type'),
      value: redFlagData ? getLevelNameDisplay(redFlagData.levelName!) : 'N/A',
    },
    {
      label: t('visitors.redFlag.modal.fields.firstName'),
      value: capitalizeName(redFlagData?.firstName ?? null),
    },
    {
      label: t('visitors.redFlag.modal.fields.lastName'),
      value: capitalizeName(redFlagData?.lastName ?? null),
    },
    {
      label: t('visitors.redFlag.modal.fields.middleName'),
      value: capitalizeName(redFlagData?.middleName ?? null),
    },
  ].filter((item) => item.value);

  const dateData = [
    {
      label: t('visitors.redFlag.modal.fields.effectiveDate'),
      value: redFlagData?.effectiveDate,
    },
    {
      label: t('visitors.redFlag.modal.fields.expireDate'),
      value: opTime(redFlagData?.expireDate).format(DATE_FORMAT),
    },
  ].filter((item) => opTime(item.value).isValid());

  const dobData = [
    {
      label: t('visitors.redFlag.modal.fields.dob'),
      value: opTime(redFlagData?.dateOfBirth).format(DATE_FORMAT),
    },
  ].filter((item) => opTime(item.value).isValid());

  return (
    <OpModal
      title={t('visitors.redFlag.modal.title')}
      open={open}
      onOk={onClose}
      onCancel={onClose}
      okText={t('visitors.redFlag.modal.close')}
      cancelButtonProps={{ style: { display: 'none' } }}
      width={720}
    >
      <div
        ref={scrollableContainerRef}
        style={{ overflowY: 'auto', maxHeight: '50vh' }}
      >
        {profileData.length > 0 && (
          <OpList
            bordered
            dataSource={profileData}
            renderItem={(item) => {
              const profileItem = item as { label: string; value: ReactNode };
              return renderListItem(profileItem.label, profileItem.value);
            }}
          />
        )}
        {profileData.length > 0 && <div style={{ margin: '16px 0' }} />}
        {dateData.length > 0 && (
          <OpList
            bordered
            dataSource={dateData}
            renderItem={(item) => {
              const dateItem = item as { label: string; value: ReactNode };
              return renderListItem(dateItem.label, dateItem.value);
            }}
          />
        )}
        {dateData.length > 0 && <div style={{ margin: '16px 0' }} />}
        {dobData.length > 0 && (
          <OpList
            bordered
            dataSource={dobData}
            renderItem={(item) => {
              const dobItem = item as { label: string; value: ReactNode };
              return renderListItem(dobItem.label, dobItem.value);
            }}
          />
        )}
        {redFlagData && redFlagData.data && redFlagData.data.length > 0 && (
          <>
            <div style={{ margin: '16px 0' }} />
            <OpList
              bordered
              header={
                <div style={{ color: 'var(--colorPrimary)' }}>
                  {t('visitors.redFlag.modal.fields.additional')}
                </div>
              }
              dataSource={redFlagData.data}
              renderItem={(item) => {
                const additionalItem = item as {
                  data: string;
                  value: string;
                };
                return (
                  <OpList.Item>
                    <Text strong>{additionalItem.data}:</Text>{' '}
                    {additionalItem.value}
                  </OpList.Item>
                );
              }}
            />
          </>
        )}
        {sortedLogs.length > 0 && (
          <>
            <div style={{ margin: '16px 0' }} />
            <OpList
              bordered
              header={
                <div style={{ color: 'var(--colorPrimary)' }}>
                  {t('visitors.redFlag.modal.fields.logs')}
                </div>
              }
              dataSource={sortedLogs}
              renderItem={(item) => {
                const logItem = item as { log: string; timestamp: string };
                return (
                  <OpList.Item>
                    <OpList.Item.Meta
                      title={logItem.log}
                      description={opTime(logItem.timestamp)
                        .utc(true)
                        .local()
                        .format(DATE_TIME_AM_PM_FORMAT)}
                    />
                  </OpList.Item>
                );
              }}
            />
          </>
        )}
      </div>
    </OpModal>
  );
};

export default RedFlagDetailsModal;
